var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { Auth } from '@freelancer/auth';
import { Store } from '@ngrx/store';
import * as Rx from 'rxjs';
import { distinctUntilChanged, filter, map, pairwise, publishReplay, refCount, startWith, switchMap, take, tap, } from 'rxjs/operators';
import { StoreBackend } from './backend';
import { DatastoreCollection } from './datastore-collection';
import { DatastoreDocument } from './datastore-document';
import { arrayIsShallowEqual, isDefined, objectFilterByKeys, sameElements, setDiff, toNumber, toObservable, } from './helpers';
import { DatastoreMissingModuleError } from './missing-module-error';
import { duplicateFirst } from './operators/duplicateFirst';
import { select } from './operators/select';
import { RequestStatusHandler } from './request-status-handler.service';
import { SearchManager } from './search';
import { flattenQuery, selectEntitiesForQuery } from './store.helpers';
import { WebSocketService } from './websocket';
var Datastore = /** @class */ (function () {
    function Datastore(store$, storeBackend, webSocketService, searchManager, requestStatusHandler, auth) {
        this.store$ = store$;
        this.storeBackend = storeBackend;
        this.webSocketService = webSocketService;
        this.searchManager = searchManager;
        this.requestStatusHandler = requestStatusHandler;
        this.auth = auth;
        this.REFETCH_TIMEOUT = 10000;
    }
    /**
     * Returns a collection of objects, customised by query.
     *
     * @param collectionName Collection name
     * @param queryFn Query to refine results
     */
    Datastore.prototype.collection = function (collectionName, queryFn) {
        var _this = this;
        var flattenedQuery$ = flattenQuery(queryFn);
        var refStream$ = Rx.combineLatest(toObservable(collectionName), toObservable(this.auth.authState$), flattenedQuery$).pipe(map(function (_a) {
            var collection = _a[0], authState = _a[1], _b = _a[2], limit = _b.limit, params = _b.params, order = _b.order;
            return ({
                path: { collection: collection, authUid: authState ? authState.userId : '0' },
                query: { limit: limit, params: params },
                order: order,
            });
        }));
        // Requests to the backend, further processed in `request-data.effect.ts`
        var requestStream$ = refStream$.pipe(map(function (ref) {
            var request = {
                type: ref.path.collection,
                ref: ref,
                clientRequestIds: [_this.generateClientRequestId()],
            };
            return request;
        }), tap(function (request) {
            // Only dispatch to store on a non-null query
            if (request.ref.query && request.ref.query.params) {
                var action = {
                    type: 'REQUEST_DATA',
                    payload: request,
                };
                _this.store$.dispatch(action);
            }
        }), publishReplay(1), refCount());
        var sourceStream$ = requestStream$.pipe(tap(function (request) {
            _this.requestStatusHandler.update(request, {
                ready: false,
            });
        }), switchMap(function (request) {
            var ref = request.ref, _a = request.ref, _b = _a.path, collection = _b.collection, authUid = _b.authUid, query = _a.query;
            var defaultOrder = _this.storeBackend.defaultOrder(collection);
            var source$ = // grab the collection items from the store
             query && !query.params
                ? Rx.of([]) // immediately emit on null query
                : _this.store$.pipe(tap(function (source) { return _this.verifyModuleIsImported(collection, source); }), // TODO: T75102
                select(collection, authUid), filter(isDefined), map(function (storeSlice) {
                    return selectEntitiesForQuery(storeSlice, ref, defaultOrder);
                }), filter(isDefined));
            return source$.pipe(tap(function () {
                return _this.requestStatusHandler.update(request, {
                    ready: true,
                });
            }), distinctUntilChanged(arrayIsShallowEqual));
        }), publishReplay(1), refCount());
        var statusStream$ = Rx.combineLatest(requestStream$.pipe(switchMap(function (request) { return _this.requestStatusHandler.get$(request); })), sourceStream$.pipe(startWith(undefined))).pipe(map(function (_a) {
            var status = _a[0];
            return status;
        }), distinctUntilChanged(), publishReplay(1), refCount());
        return new DatastoreCollection(refStream$, this.storeBackend, statusStream$, Rx.combineLatest(statusStream$.pipe(startWith(undefined)), sourceStream$).pipe(map(function (_a) {
            var source = _a[1];
            return source;
        }), distinctUntilChanged()));
    };
    Datastore.prototype.document = function (collectionName, 
    // document id defaults to false if not provided, i.e. the first object of
    // the collection
    documentId$, documentQuery) {
        var _this = this;
        if (documentId$ === void 0) { documentId$ = Rx.of(0); }
        var refStream$ = Rx.combineLatest(toObservable(collectionName), toObservable(this.auth.authState$), toObservable(documentId$).pipe(distinctUntilChanged())).pipe(map(function (_a) {
            var collection = _a[0], authState = _a[1], id = _a[2];
            var _b;
            return documentQuery
                ? {
                    path: {
                        collection: collection,
                        authUid: authState ? authState.userId : '0',
                    },
                    query: {
                        limit: 1,
                        params: (_b = {},
                            _b[documentQuery.index] = [
                                {
                                    name: documentQuery.index,
                                    condition: documentQuery.caseInsensitive
                                        ? 'equalsIgnoreCase'
                                        : '==',
                                    value: id,
                                },
                            ],
                            _b),
                    },
                }
                : {
                    path: {
                        collection: collection,
                        authUid: authState ? authState.userId : '0',
                        ids: [id.toString()],
                    },
                };
        }));
        var requestStream$ = refStream$.pipe(map(function (ref) { return ({
            type: ref.path.collection,
            ref: ref,
            clientRequestIds: [_this.generateClientRequestId()],
        }); }), tap(function (request) {
            var action = {
                type: 'REQUEST_DATA',
                payload: request,
            };
            _this.store$.dispatch(action);
        }), publishReplay(1), refCount());
        // TODO: Rx.merge causes double emission - T53648
        var sourceStream$ = (collectionName === 'onlineOffline' // include websocket subscription stream
            ? Rx.merge(Rx.combineLatest(this.webSocketService.subscribeOnlineOffline(requestStream$.pipe(map(function (request) { return request.ref.path.ids; }), filter(isDefined))), requestStream$).pipe(map(function (_a) {
                var ss = _a[0], req = _a[1];
                return req;
            })), requestStream$)
            : requestStream$).pipe(tap(function (request) {
            return _this.requestStatusHandler.update(request, {
                ready: false,
            });
        }), switchMap(function (request) {
            var ref = request.ref, _a = request.ref.path, collection = _a.collection, authUid = _a.authUid;
            return _this.store$.pipe(tap(function (source) { return _this.verifyModuleIsImported(collection, source); }), // TODO: T75102
            select(collection, authUid), filter(isDefined), map(function (state) { return _this.fetchSingleObject(state, ref); }), filter(isDefined), tap(function () {
                return _this.requestStatusHandler.update(request, {
                    ready: true,
                });
            }), distinctUntilChanged());
        }), publishReplay(1), refCount());
        var statusStream$ = Rx.combineLatest(requestStream$.pipe(switchMap(function (request) { return _this.requestStatusHandler.get$(request); })), sourceStream$.pipe(startWith(undefined))).pipe(map(function (_a) {
            var status = _a[0];
            return status;
        }), distinctUntilChanged(), publishReplay(1), refCount());
        return new DatastoreDocument(refStream$, this.storeBackend, statusStream$, Rx.combineLatest(statusStream$.pipe(startWith(undefined)), sourceStream$).pipe(map(function (_a) {
            var source = _a[1];
            return source;
        }), distinctUntilChanged()));
    };
    Datastore.prototype.fetchSingleObject = function (storeSlice, ref) {
        var _a = ref.path, collection = _a.collection, ids = _a.ids;
        if (ids) {
            return storeSlice.entities[ids[0]] && storeSlice.entities[ids[0]].entity;
        }
        var defaultOrder = this.storeBackend.defaultOrder(collection);
        var entities = selectEntitiesForQuery(storeSlice, ref, defaultOrder);
        return entities ? entities[0] : undefined;
    };
    /**
     * Returns a list of objects from a collection specified by id.
     * If an observable is passed in for `objectIds` then only the delta will be
     * refetched from the network.
     *
     * @param collectionName Collection name
     * @param objectIds$
     */
    Datastore.prototype.objects = function (collectionName, objectIds$) {
        var _this = this;
        var requestStream$ = Rx.combineLatest([
            toObservable(collectionName),
            toObservable(this.auth.authState$),
            // FIXME: this should be ReadonlyArray<number> | ReadonlyArray<string>
            // https://github.com/Microsoft/TypeScript/issues/7294
            toObservable(objectIds$).pipe(distinctUntilChanged(arrayIsShallowEqual)),
        ], function (collection, authState, ids) {
            var path = {
                collection: collection,
                authUid: authState ? authState.userId : '0',
                ids: ids.map(function (id) { return id.toString(); }),
            };
            return { path: path };
        }).pipe(
        // Add an path at the start with an empty list of ids
        duplicateFirst(function (_a) {
            var _b = _a.path, collection = _b.collection, authUid = _b.authUid;
            return ({
                path: { collection: collection, authUid: authUid, ids: [] },
            });
        }), pairwise(), // Use pairwise to calculate and fetch new objects only
        switchMap(function (paths) {
            var _a = paths[0].path, collection = _a.collection, authUid = _a.authUid;
            return _this.store$.pipe(select(collection, authUid), take(1), map(function (data) { return ({ paths: paths, data: data }); }));
        }), map(function (_a) {
            var _b = _a.paths, idsOrig = _b[0].path.ids, _c = _b[1].path, collection = _c.collection, authUid = _c.authUid, idsNew = _c.ids, data = _a.data;
            var newIds = setDiff(idsNew, idsOrig).filter(function (id) {
                return data && data.entities[id]
                    ? toNumber(data.entities[id].timeFetched) + _this.REFETCH_TIMEOUT <
                        Date.now()
                    : true;
            });
            var action = {
                type: 'REQUEST_DATA',
                payload: {
                    type: collection,
                    ref: { path: { collection: collection, authUid: authUid, ids: newIds } },
                    clientRequestIds: [_this.generateClientRequestId()],
                },
            };
            if (newIds.length) {
                _this.store$.dispatch(action);
            }
            return __assign({}, action.payload, { ref: { path: { collection: collection, authUid: authUid, ids: idsNew } } });
        }), publishReplay(1), refCount());
        // FIXME: This will double emit for `collection === 'onlineOffline'` due to Rx.merge
        var sourceStream$ = collectionName === 'onlineOffline' // include websocket subscription stream
            ? Rx.merge(Rx.combineLatest(this.webSocketService.subscribeOnlineOffline(requestStream$.pipe(map(function (_a) {
                var _b = _a.ref.path, collection = _b.collection, authUid = _b.authUid, ids = _b.ids;
                return ids;
            }))), requestStream$).pipe(map(function (_a) {
                var ss = _a[0], ref = _a[1];
                return ref;
            })), requestStream$)
            : requestStream$;
        return sourceStream$.pipe(switchMap(function (_a) {
            var _b = _a.ref.path, collection = _b.collection, authUid = _b.authUid, ids = _b.ids;
            return ids.length === 0
                ? Rx.of([])
                : _this.store$.pipe(tap(function (store) { return _this.verifyModuleIsImported(collection, store); }), // TODO: T75102
                select(collection, authUid), filter(isDefined), map(function (entitiesObject) {
                    return objectFilterByKeys(entitiesObject.entities, ids);
                }), map(function (entitiesObject) {
                    return Object.keys(entitiesObject).length
                        ? Object.values(entitiesObject)
                        : undefined;
                }), filter(isDefined), map(function (entities) { return entities.map(function (entity) { return entity.entity; }); }), distinctUntilChanged(sameElements));
        }), publishReplay(1), refCount());
    };
    // Hits an elastic search endpoint directly and returns the result.
    // The data doesn't get stored in the datastore (at least yet).
    Datastore.prototype.search = function (key, query$) {
        var _this = this;
        return this.storeBackend.search(key, toObservable(query$)).pipe(map(function (result) { return _this.searchManager.transform(key, result); }), publishReplay(1), refCount());
    };
    /* Creates a single object.
     */
    Datastore.prototype.createObject = function (collectionName, object, extra) {
        var _this = this;
        return Rx.combineLatest([toObservable(collectionName), toObservable(this.auth.authState$)], function (collection, authState) {
            var path = {
                collection: collection,
                authUid: authState ? authState.userId : '0',
            };
            return { path: path };
        })
            .pipe(take(1), switchMap(function (ref) { return _this.storeBackend.push(ref, object, extra); }))
            .toPromise();
    };
    Datastore.prototype.generateClientRequestId = function () {
        return Math.random()
            .toString(36)
            .substr(2, 16);
    };
    Datastore.prototype.verifyModuleIsImported = function (collection, state) {
        if (!(collection in state)) {
            throw new DatastoreMissingModuleError(collection);
        }
    };
    return Datastore;
}());
export { Datastore };
